<template>
  <div class="page-stack">
    <a-card title="Профиль" :bordered="false" :loading="loading">
      <a-descriptions :column="1" bordered>
        <a-descriptions-item label="Имя">
          {{ me?.name || '—' }}
        </a-descriptions-item>

        <a-descriptions-item label="Телефон">
          {{ me?.phone || '—' }}
        </a-descriptions-item>

        <a-descriptions-item label="Логин">
          {{ me?.login || '—' }}
        </a-descriptions-item>

        <a-descriptions-item label="Роль">
          {{ label(me?.role) }}
        </a-descriptions-item>
      </a-descriptions>
    </a-card>

    <a-card title="Видимость приемов пищи" :bordered="false" :loading="loading">
      <a-form layout="vertical">
        <a-form-item
          v-for="meal in mealOptions"
          :key="meal.key"
          :label="meal.title"
        >
          <a-switch v-model:checked="mealVisibility[meal.key]" />
        </a-form-item>
      </a-form>
    </a-card>

    <a-card title="Смена пароля" :bordered="false">
      <a-form layout="vertical">
        <a-form-item label="Текущий пароль">
          <a-input-password v-model:value="passwordForm.current_password" />
        </a-form-item>

        <a-form-item label="Новый пароль">
          <a-input-password v-model:value="passwordForm.new_password" />
        </a-form-item>

        <a-form-item label="Повтор нового пароля">
          <a-input-password v-model:value="passwordForm.new_password2" />
        </a-form-item>

        <a-button type="primary" @click="savePassword" :loading="savingPassword">
          Сменить пароль
        </a-button>
      </a-form>
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted, onBeforeUnmount } from 'vue'
import { message } from 'ant-design-vue'
import { getMe, changePassword, updateMealVisibility } from '../../api/profile'
import { label } from '../../utils/labels'

const loading = ref(false)
const savingPassword = ref(false)
const me = ref(null)
const suppressVisibilitySave = ref(true)
const visibilitySaveTimer = ref(null)

const passwordForm = ref({
  current_password: '',
  new_password: '',
  new_password2: '',
})

const mealOptions = [
  { key: 'BREAKFAST', title: 'Завтрак' },
  { key: 'LUNCH', title: 'Обед' },
  { key: 'SNACK', title: 'Полдник' },
  { key: 'DINNER', title: 'Ужин' },
]

const mealVisibility = reactive({
  BREAKFAST: true,
  LUNCH: true,
  SNACK: true,
  DINNER: true,
})

const savedMealVisibility = reactive({
  BREAKFAST: true,
  LUNCH: true,
  SNACK: true,
  DINNER: true,
})

function applyMealVisibility(target, value = {}) {
  for (const meal of mealOptions) {
    target[meal.key] = value[meal.key] ?? true
  }
}

function buildMealVisibilityPayload() {
  return {
    BREAKFAST: !!mealVisibility.BREAKFAST,
    LUNCH: !!mealVisibility.LUNCH,
    SNACK: !!mealVisibility.SNACK,
    DINNER: !!mealVisibility.DINNER,
  }
}

async function load() {
  loading.value = true
  suppressVisibilitySave.value = true

  if (visibilitySaveTimer.value) {
    clearTimeout(visibilitySaveTimer.value)
    visibilitySaveTimer.value = null
  }

  try {
    const res = await getMe()
    me.value = res.data.data
    applyMealVisibility(mealVisibility, me.value?.meal_visibility)
    applyMealVisibility(savedMealVisibility, me.value?.meal_visibility)
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка загрузки профиля')
  } finally {
    loading.value = false
    setTimeout(() => {
      suppressVisibilitySave.value = false
    }, 0)
  }
}

async function savePassword() {
  if (!passwordForm.value.new_password) {
    message.warning('Введите новый пароль')
    return
  }

  if (passwordForm.value.new_password !== passwordForm.value.new_password2) {
    message.warning('Пароли не совпадают')
    return
  }

  savingPassword.value = true
  try {
    await changePassword(
      passwordForm.value.current_password,
      passwordForm.value.new_password
    )
    message.success('Пароль изменён')
    passwordForm.value = {
      current_password: '',
      new_password: '',
      new_password2: '',
    }
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка смены пароля')
  } finally {
    savingPassword.value = false
  }
}

async function saveMealVisibility() {
  const payload = buildMealVisibilityPayload()

  if (
    payload.BREAKFAST === savedMealVisibility.BREAKFAST &&
    payload.LUNCH === savedMealVisibility.LUNCH &&
    payload.SNACK === savedMealVisibility.SNACK &&
    payload.DINNER === savedMealVisibility.DINNER
  ) {
    return
  }

  try {
    await updateMealVisibility(payload)
    applyMealVisibility(savedMealVisibility, payload)

    if (me.value) {
      me.value.meal_visibility = { ...payload }
    }
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка сохранения настроек')
  }
}

watch(
  () => mealOptions.map(meal => mealVisibility[meal.key]),
  () => {
    if (suppressVisibilitySave.value) {
      return
    }

    if (visibilitySaveTimer.value) {
      clearTimeout(visibilitySaveTimer.value)
    }

    visibilitySaveTimer.value = setTimeout(() => {
      visibilitySaveTimer.value = null
      saveMealVisibility()
    }, 400)
  }
)

onBeforeUnmount(() => {
  if (visibilitySaveTimer.value) {
    clearTimeout(visibilitySaveTimer.value)
  }
})

onMounted(load)
</script>

<style scoped>
.page-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}
</style>
